/**
 * Developer-visible logging for platform-gating decisions.
 *
 * The app intentionally hides or disables features (notably PPPC, which is
 * macOS-only) when the current app's platform doesn't qualify. These events
 * are noisy in normal use but invaluable when debugging edge cases like:
 *   - an app whose platform field is missing/misspelled ("MacOS" vs "macos")
 *   - a wizard where the user toggled platform mid-flow and inputs locked
 *   - a Configure-now CTA that didn't appear because pppcData was undefined
 *
 * Logs go to `console.debug` (filterable in DevTools) and are forwarded to a
 * window-level CustomEvent so an analytics layer can subscribe without
 * coupling this util to any vendor SDK.
 *
 * Usage:
 *   logPlatformGating({
 *     feature: "pppc",
 *     surface: "ApplicationDetailPage",
 *     reason: "non-macos-platform",
 *     platform: app.platform,
 *     bundleId: app.bundleId,
 *   });
 */

export type PlatformGatedFeature = "pppc" | "app-config";

export type PlatformGatingReason =
  | "non-macos-platform"
  | "missing-bundle-id"
  | "platform-changed-mid-wizard"
  | "no-existing-policy";

export interface PlatformGatingEvent {
  feature: PlatformGatedFeature;
  /** Component / page emitting the event — e.g. "AddApplicationPage:PppcStep". */
  surface: string;
  reason: PlatformGatingReason;
  platform?: string | null;
  bundleId?: string | null;
  /** Free-form extra context for debugging. */
  extra?: Record<string, unknown>;
}

const EVENT_NAME = "smplify:platform-gated";

/**
 * Tracks the last event signature per surface so we don't spam the console
 * on every render. A surface only re-logs when its meaningful inputs change.
 */
const lastSignatureBySurface = new Map<string, string>();

const buildSignature = (e: PlatformGatingEvent): string =>
  [e.feature, e.reason, e.platform ?? "", e.bundleId ?? ""].join("|");

export function logPlatformGating(event: PlatformGatingEvent): void {
  const signature = buildSignature(event);
  const surfaceKey = `${event.surface}::${event.feature}`;
  if (lastSignatureBySurface.get(surfaceKey) === signature) return;
  lastSignatureBySurface.set(surfaceKey, signature);

  // Dev-visible structured log. console.debug is hidden by default in Chrome
  // unless "Verbose" is enabled, so it doesn't pollute normal sessions.
  // eslint-disable-next-line no-console
  console.debug(
    `[platform-gated] ${event.feature} hidden on ${event.surface} (${event.reason})`,
    {
      platform: event.platform ?? null,
      bundleId: event.bundleId ?? null,
      ...(event.extra ?? {}),
    },
  );

  // Fan out to any listening analytics layer without coupling this util to
  // a specific SDK. Subscribers can call `window.addEventListener(EVENT_NAME, ...)`.
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(EVENT_NAME, { detail: event }),
      );
    } catch {
      // Best-effort: never let analytics break the UI.
    }
  }
}

export const PLATFORM_GATING_EVENT_NAME = EVENT_NAME;
